/**
 * @file        src/lib/fareDisplay.jsx
 * @description Shared display helpers for the fare card and fare details:
 *              feeText() prints a fare-rule fee as ONE total, and <BoldKg>
 *              bolds every baggage weight inside a supplier allowance string.
 * @author      Kinetika Labs
 * @created     2026-09-11
 * @changelog
 *   - 2026-10-26 Claude — feeText() no longer bifurcates ("₹3,000.00 + ₹115.00").
 *                          TripJack's own airline-fee/service-fee split was never
 *                          something the customer needed to see, and the figures
 *                          callers now pass already include our own per-passenger
 *                          amendment-markup estimate (fareRuleSummary.js), so a
 *                          "+" display would wrongly suggest only TWO numbers were
 *                          ever involved. One merged total, to the paise.
 *   - 2026-09-11 Claude — Added fmtHours() for the fare card's fee slabs.
 *   - 2026-09-11 Claude — Initial.
 */
import { inr2 } from './format.js';

/**
 * "₹3,615.00" — amount + addFee, merged to one figure, to the paise. Callers
 * must only pass an amount the supplier actually SENT: absent is not zero.
 */
export function feeText(amount, addFee = 0) {
  const a = Number(amount) || 0;
  const f = Number(addFee) || 0;
  return inr2(a + f);
}

/**
 * A fare-rule hour mark (TripJack st/et — hours before departure) the way the
 * airline states it: whole days once it divides evenly, hours otherwise.
 * 5 → "5 hrs", 74 → "74 hrs", 8760 → "365 days", 1 → "1 hr".
 */
export function fmtHours(v) {
  const n = Number(v);
  if (v == null || v === '' || !Number.isFinite(n)) return '—';
  if (n >= 24 && n % 24 === 0) { const d = n / 24; return `${d} day${d === 1 ? '' : 's'}`; }
  return `${n} hr${n === 1 ? '' : 's'}`;
}

// A weight with its unit: "15 Kg", "25 KG", "7Kgs", "20.5 kg". Captured, so
// split() keeps each match at an odd index.
const KG = /(\d+(?:\.\d+)?\s*kgs?\b)/i;

/** `text` with every KG value bold: "25 Kg (01 Piece only)" → **25 Kg** (01 Piece only). */
export function BoldKg({ text }) {
  const parts = String(text ?? '').split(new RegExp(KG.source, 'gi'));
  return <>{parts.map((p, i) => (i % 2 ? <strong key={i} className="font-bold">{p}</strong> : p))}</>;
}
